/**
 * features/letters/letterTemplates.ts
 * Definitions for each regulatory letter: input fields, default
 * recipient, subject, and a prefilled body the user can edit before
 * generating. Body text is fully editable in the editor.
 */
import type { CompanySettings, LetterKind } from '@/types';

export interface LetterField {
  key: string;
  label: string;
  type?: 'text' | 'textarea' | 'date';
  placeholder?: string;
}

export interface LetterTemplate {
  kind: LetterKind;
  label: string;
  blurb: string;
  fields: LetterField[];
  recipient: (v: Record<string, string>) => string;
  subject: (v: Record<string, string>) => string;
  body: (v: Record<string, string>, c: CompanySettings) => string;
}

const companyName = (c: CompanySettings) => c.legalName || c.tradingName || 'the Company';
const director = (c: CompanySettings) => c.directorName || '[Director name]';

export const LETTER_TEMPLATES: Record<LetterKind, LetterTemplate> = {
  account_opening: {
    kind: 'account_opening',
    label: 'Account opening letter',
    blurb: 'Request to open a bank or mobile-money account (e.g. Orange Money).',
    fields: [
      { key: 'provider', label: 'Institution / provider', placeholder: 'Orange Money Botswana' },
      { key: 'accountType', label: 'Account type', placeholder: 'Business / merchant account' },
      { key: 'branch', label: 'Branch / office (optional)', placeholder: 'Gaborone' },
    ],
    recipient: (v) => `The Manager\n${v.provider || '[Provider]'}\n${v.branch || 'Gaborone'}\nBotswana`,
    subject: (v) => `Application to Open a ${v.accountType || 'Business'} Account`,
    body: (v, c) =>
      `Dear Sir/Madam,\n\n` +
      `We, ${companyName(c)} (Registration No. ${c.registrationNumber}${c.tin ? `, TIN ${c.tin}` : ''}), ` +
      `hereby apply to open a ${v.accountType || 'business'} account with ${v.provider || '[Provider]'}.\n\n` +
      `The company is duly registered in the Republic of Botswana and operates from ${c.physicalAddress}. ` +
      `We confirm that ${director(c)} is authorised to act on behalf of the company in respect of this account, ` +
      `including its operation and the execution of related instructions.\n\n` +
      `Kindly advise on any further documentation required to process this application. ` +
      `We look forward to your favourable response.`,
  },

  employee_confirmation: {
    kind: 'employee_confirmation',
    label: 'Employee confirmation letter',
    blurb: 'Confirms a person’s employment, role and standing with the company.',
    fields: [
      { key: 'employeeName', label: 'Employee name', placeholder: 'Full name' },
      { key: 'position', label: 'Position', placeholder: 'e.g. Software Developer' },
      { key: 'startDate', label: 'Start date', type: 'date' },
      { key: 'grossSalary', label: 'Monthly gross (optional)', placeholder: 'P 0.00' },
      { key: 'purpose', label: 'Addressed to / purpose', placeholder: 'To whom it may concern' },
    ],
    recipient: (v) => v.purpose?.trim() || 'TO WHOM IT MAY CONCERN',
    subject: (v) => `Confirmation of Employment — ${v.employeeName || '[Employee]'}`,
    body: (v, c) =>
      `Dear Sir/Madam,\n\n` +
      `This letter serves to confirm that ${v.employeeName || '[Employee]'} is employed by ${companyName(c)} ` +
      `in the position of ${v.position || '[Position]'}${v.startDate ? `, since ${new Date(v.startDate).toLocaleDateString('en-BW', { day: '2-digit', month: 'long', year: 'numeric' })}` : ''}.\n\n` +
      (v.grossSalary?.trim()
        ? `The employee earns a monthly gross remuneration of ${v.grossSalary}. `
        : '') +
      `${v.employeeName || 'The employee'} remains in good standing with the company.\n\n` +
      `This letter is issued at the employee’s request for whatever purpose it may legally serve. ` +
      `Should you require any further information, please do not hesitate to contact us.`,
  },

  board_resolution: {
    kind: 'board_resolution',
    label: 'Board resolution',
    blurb: 'A directors’ resolution authorising an action (e.g. opening an account).',
    fields: [
      { key: 'meetingDate', label: 'Date of resolution', type: 'date' },
      { key: 'purpose', label: 'Purpose', placeholder: 'opening of an Orange Money account' },
      { key: 'resolutions', label: 'Resolved that…', type: 'textarea', placeholder: 'One resolution per line' },
    ],
    recipient: () => 'TO WHOM IT MAY CONCERN',
    subject: (v) => `Resolution of the Board of Directors — ${v.purpose || 'Authorisation'}`,
    body: (v, c) => {
      const date = v.meetingDate
        ? new Date(v.meetingDate).toLocaleDateString('en-BW', { day: '2-digit', month: 'long', year: 'numeric' })
        : '[date]';
      const items = (v.resolutions || '')
        .split('\n')
        .map((l) => l.trim())
        .filter(Boolean);
      const resolved =
        items.length > 0
          ? items.map((it, i) => `${i + 1}. ${it}`).join('\n')
          : `1. That the company proceed with the ${v.purpose || '[purpose]'}.\n2. That ${director(c)} be and is hereby authorised to sign all documents and give all instructions necessary to give effect to this resolution.`;
      return (
        `EXTRACT OF A RESOLUTION passed by the Board of Directors of ${companyName(c)} ` +
        `(Registration No. ${c.registrationNumber}) held on ${date} at ${c.physicalAddress}.\n\n` +
        `IT WAS RESOLVED THAT:\n\n${resolved}\n\n` +
        `We certify that the above is a true extract of the resolution duly passed and recorded in the minutes of the company.`
      );
    },
  },

  general: {
    kind: 'general',
    label: 'General letter',
    blurb: 'A blank company letter on letterhead.',
    fields: [
      { key: 'recipient', label: 'Recipient block', type: 'textarea', placeholder: 'Name\nOrganisation\nAddress' },
      { key: 'subject', label: 'Subject', placeholder: 'Subject line' },
      { key: 'bodyText', label: 'Body', type: 'textarea', placeholder: 'Write the letter…' },
    ],
    recipient: (v) => v.recipient?.trim() || 'TO WHOM IT MAY CONCERN',
    subject: (v) => v.subject?.trim() || 'Letter',
    body: (v) => v.bodyText?.trim() || '',
  },
};

export const LETTER_LIST = Object.values(LETTER_TEMPLATES);
